import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, User, Mail, Save, Loader2, Settings, Bell, Crown, CreditCard, Plus, Scan, Zap } from 'lucide-react';
import SubscriptionManagement from './SubscriptionManagement';

export const UserProfileModal = ({ user, onSave, onClose, isLoading = false }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    notificationPreferences: {
      itemUpdates: user?.notificationPreferences?.itemUpdates ?? true,
      storyAdditions: user?.notificationPreferences?.storyAdditions ?? true,
      promotionalEmails: user?.notificationPreferences?.promotionalEmails ?? false
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (pref, value) => {
    setFormData(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [pref]: value
      }
    }));
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            My Profile
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        {/* Tab Navigation */}
        <div className="border-b">
          <div className="flex px-6">
            {[
              { id: 'profile', label: 'Profile Settings', icon: User },
              { id: 'actions', label: 'Quick Actions', icon: Zap },
              { id: 'subscription', label: 'Subscription', icon: Crown },
              { id: 'notifications', label: 'Notifications', icon: Bell }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <CardContent className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="pl-10"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="pl-10"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Quick Actions Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Common actions for managing your iWasHere.me experience.
                </p>
              </div>

              <div className="grid gap-4">
                {/* Add New Item */}
                <div className="p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-2">Add New Item for Tracking</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Create a new trackable item and generate its QR code to start its journey.
                      </p>
                    </div>
                    <Plus className="w-5 h-5 text-emerald-600 mt-1 ml-4" />
                  </div>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => {
                      navigate('/create-item');
                      onClose();
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Item
                  </Button>
                </div>

                {/* Batch Story Processing - Only for Premium/Admin users
                    NOTE: The user-facing entry point for free/premium/admin users
                    lives in `UserProfile` (UserAuth.jsx), which is the modal opened
                    by the Header avatar. This block is retained for the legacy
                    UserProfileModal flow only. */}
                {(user.role === 'admin' || user.subscription === 'premium') && (
                  <div className="p-4 border border-yellow-200 rounded-lg hover:border-yellow-300 transition-colors bg-yellow-50/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          Batch Story Processing
                          <Crown className="w-4 h-4 text-yellow-600 ml-2" />
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Register multiple existing QR codes or add stories to items quickly using your phone&apos;s camera.
                        </p>
                      </div>
                      <Scan className="w-5 h-5 text-yellow-600 mt-1 ml-4" />
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-2 border-yellow-600 text-yellow-600 hover:bg-yellow-50"
                      onClick={() => {
                        navigate('/bulk-register');
                        onClose();
                      }}
                    >
                      <Scan className="w-4 h-4 mr-2" />
                      Start Batch Story Processing
                    </Button>
                  </div>
                )}

                {/* Upgrade Prompt for Free Users */}
                {user.subscription === 'free' && (
                  <div className="p-4 border border-blue-200 rounded-lg bg-blue-50/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2">Unlock Premium Features</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Get access to bulk registration, unlimited items, and priority support.
                        </p>
                      </div>
                      <Crown className="w-5 h-5 text-blue-600 mt-1 ml-4" />
                    </div>
                    <Button 
                      variant="outline"
                      className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                      onClick={() => {
                        navigate('/store');
                        onClose();
                      }}
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      View Subscription Plans
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <SubscriptionManagement 
              userEmail={user?.email}
              onSubscriptionChange={(newPlan) => {
                // Handle subscription change if needed
                console.log('Subscription changed to:', newPlan);
              }}
            />
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Email Notifications
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Choose which notifications you'd like to receive via email.
                </p>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Item Updates</h4>
                      <p className="text-sm text-gray-600">
                        Get notified when someone adds a story to your items
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notificationPreferences.itemUpdates}
                        onChange={(e) => handleNotificationChange('itemUpdates', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">New Stories</h4>
                      <p className="text-sm text-gray-600">
                        Get updates about new stories in the community
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notificationPreferences.storyAdditions}
                        onChange={(e) => handleNotificationChange('storyAdditions', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Promotional Emails</h4>
                      <p className="text-sm text-gray-600">
                        Receive updates about new features and special offers
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notificationPreferences.promotionalEmails}
                        onChange={(e) => handleNotificationChange('promotionalEmails', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={() => onSave(formData)} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Preferences
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};